import React, { useState, useEffect } from 'react';
import { BookOpen, RefreshCw, Filter } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { getAccountClassColor, getAccountTypeColor, getAccountStatusColor } from '../../utils/statusColors';

interface ChartOfAccount {
  _id: string;
  code: string;
  name: string;
  type: string;
  class?: string;
  status: string;
  description?: string;
  taxType?: string;
  bankAccountNumber?: string;
  syncedFromXero: boolean;
  isActive: boolean;
}

export const ChartOfAccounts: React.FC = () => {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClass, setSelectedClass] = useState<string>('ALL');
  const [selectedType, setSelectedType] = useState<string>('ALL');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    filterAccounts();
  }, [accounts, selectedClass, selectedType]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/chart-of-accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setAccounts(data.data);
      } else {
        setError(data.message || 'Failed to fetch chart of accounts');
      }
    } catch (err) {
      console.error('Fetch chart of accounts error:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const filterAccounts = () => {
    let filtered = accounts;

    if (selectedClass !== 'ALL') {
      filtered = filtered.filter(acc => acc.class === selectedClass);
    }

    if (selectedType !== 'ALL') {
      filtered = filtered.filter(acc => acc.type === selectedType);
    }

    setFilteredAccounts(filtered);
  };

  const accountClasses = ['ALL', 'ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-12 h-12 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center mr-4">
            <BookOpen className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('admin.chartOfAccounts.title')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('admin.chartOfAccounts.description')}
            </p>
          </div>
        </div>
        
        <button
          onClick={fetchAccounts}
          disabled={loading}
          className="flex items-center px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t('admin.chartOfAccounts.refresh')}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-error-400">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-600 dark:text-gray-400">{t('admin.chartOfAccounts.filter')}</span>
        </div>
        
        <select
          value={selectedClass}
          onChange={(e) => setSelectedClass(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          {accountClasses.map(cls => (
            <option key={cls} value={cls}>{cls === 'ALL' ? t('admin.chartOfAccounts.allClasses') : cls}</option>
          ))}
        </select>

        {(selectedClass !== 'ALL' || selectedType !== 'ALL') && (
          <button
            onClick={() => {
              setSelectedClass('ALL');
              setSelectedType('ALL');
            }}
            className="text-sm text-accent-600 dark:text-accent-400 hover:underline"
          >
            {t('admin.chartOfAccounts.clearFilters')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('admin.chartOfAccounts.loading')}</p>
        </div>
      ) : filteredAccounts.length === 0 ? (
        <div className="text-center py-8">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            {accounts.length === 0 
              ? t('admin.chartOfAccounts.noAccounts')
              : t('admin.chartOfAccounts.noMatch')
            }
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-600">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('admin.chartOfAccounts.tableHeaders.code')}
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('admin.chartOfAccounts.tableHeaders.name')}
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('admin.chartOfAccounts.tableHeaders.class')}
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('admin.chartOfAccounts.tableHeaders.type')}
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('admin.chartOfAccounts.tableHeaders.status')}
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('admin.chartOfAccounts.tableHeaders.taxType')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.map((account) => (
                <tr
                  key={account._id}
                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="py-3 px-4">
                    <span className="text-sm font-mono font-semibold text-gray-900 dark:text-gray-100">
                      {account.code}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {account.name}
                      </div>
                      {account.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {account.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {account.class && (
                      <span className={`text-xs px-2 py-1 rounded font-medium ${getAccountClassColor(account.class)}`}>
                        {account.class}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${getAccountTypeColor()}`}>
                      {account.type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${getAccountStatusColor(account.isActive, account.status)}`}>
                      {account.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {account.taxType ? (
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {account.taxType}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredAccounts.length > 0 && (
        <div className="mt-4 flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
          <span>
            {t('admin.chartOfAccounts.showing').replace('{filtered}', filteredAccounts.length.toString()).replace('{total}', accounts.length.toString()).replace('{plural}', accounts.length !== 1 ? 's' : '')}
          </span>
          {accounts.some(a => a.syncedFromXero) && (
            <div className="flex items-center text-xs text-accent-600 dark:text-accent-400">
              <img src="/xero.svg" alt="Xero" className="w-4 h-4 mr-1" />
              {t('admin.chartOfAccounts.syncedFromXero')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

